import { List, Plus, Pencil, Trash2, Layers, Zap, BarChart3 } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface AssetList {
  id: string;
  title: string;
  description: string | null;
  isShared?: boolean;
}

export function AppSidebar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [assetLists, setAssetLists] = useState<AssetList[]>([]);
  const [isFetchingLists, setIsFetchingLists] = useState(false);
  const [isSeoAdmin, setIsSeoAdmin] = useState(false);
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState<AssetList | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isMobileNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (user) {
      fetchAssetLists();
    }

    // Listen for invite changes to refresh the list
    const handleInviteChange = () => {
      if (user) {
        fetchAssetLists();
      }
    };

    window.addEventListener('invite-changed', handleInviteChange);
    return () => {
      window.removeEventListener('invite-changed', handleInviteChange);
    };
  }, [user]);

  // Probe whether the current user is allowed to view the SEO dashboard.
  // Server gates by SEO_ADMIN_EMAILS; non-admins get 403 and the link stays hidden.
  useEffect(() => {
    if (!user) {
      setIsSeoAdmin(false);
      return;
    }
    const cacheKey = `seo-admin:${user.id}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached !== null) {
      setIsSeoAdmin(cached === "1");
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.functions.invoke("seo-sites");
      if (cancelled) return;
      const ok = !error && !!data && Array.isArray((data as any).sites);
      sessionStorage.setItem(cacheKey, ok ? "1" : "0");
      setIsSeoAdmin(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const fetchAssetLists = async () => {
    if (!user) return;

    setIsFetchingLists(true);
    console.log('[AppSidebar] fetchAssetLists start', { userId: user.id, email: user.email });

    // RLS already enforces access control, so we can just fetch everything the user can see.
    const { data, error } = await supabase
      .from("asset_lists")
      .select("id, title, description, user_id")
      .order("created_at", { ascending: true });

    console.log('[AppSidebar] fetchAssetLists result', { error, count: data?.length ?? 0, data });

    if (error) {
      console.error("Error fetching asset lists:", error);
      toast.error(t('sidebar.failedToLoad'));
      setAssetLists([]);
      setIsFetchingLists(false);
      return;
    }

    setAssetLists(
      (data ?? []).map((list) => ({
        id: list.id,
        title: list.title,
        description: list.description,
        isShared: list.user_id !== user.id,
      }))
    );
    setIsFetchingLists(false);
  };

  const handleCreateList = async () => {
    if (!newListTitle.trim()) {
      toast.error(t('sidebar.pleaseEnterTitle'));
      return;
    }

    setIsCreating(true);
    const { data, error } = await supabase
      .from("asset_lists")
      .insert({
        title: newListTitle.trim(),
        description: newListDescription.trim() || null,
        user_id: user?.id,
      })
      .select()
      .single();

    setIsCreating(false);

    if (error) {
      toast.error(t('sidebar.failedToCreate'));
      console.error("Error creating asset list:", error);
      return;
    }

    toast.success(t('sidebar.assetListCreated'));
    setNewListTitle("");
    setNewListDescription("");
    setCreateDialogOpen(false);
    fetchAssetLists();
    
    if (data) {
      navigate(`/app/list/${data.id}`);
    }
  };

  const openEditDialog = (list: AssetList, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingList(list);
    setEditTitle(list.title);
    setEditDescription(list.description || "");
    setEditDialogOpen(true);
  };

  const handleUpdateList = async () => {
    if (!editTitle.trim()) {
      toast.error(t('sidebar.pleaseEnterTitle'));
      return;
    }

    if (!editingList) return;

    setIsUpdating(true);
    const { error } = await supabase
      .from("asset_lists")
      .update({
        title: editTitle.trim(),
        description: editDescription.trim() || null,
      })
      .eq("id", editingList.id)
      .eq("user_id", user?.id);

    setIsUpdating(false);

    if (error) {
      toast.error(t('sidebar.failedToUpdate'));
      console.error("Error updating asset list:", error);
      return;
    }

    toast.success(t('sidebar.assetListUpdated'));
    setEditDialogOpen(false);
    setEditingList(null);
    fetchAssetLists();
  };

  const handleDeleteList = async () => {
    if (!editingList) return;

    setIsDeleting(true);
    
    // First delete all assets in the list
    const { error: assetsError } = await supabase
      .from("assets")
      .delete()
      .eq("list_id", editingList.id)
      .eq("user_id", user?.id);

    if (assetsError) {
      toast.error(t('sidebar.failedToDeleteAssets'));
      console.error("Error deleting assets:", assetsError);
      setIsDeleting(false);
      return;
    }

    // Then delete the list
    const { error } = await supabase
      .from("asset_lists")
      .delete()
      .eq("id", editingList.id)
      .eq("user_id", user?.id);

    setIsDeleting(false);

    if (error) {
      toast.error(t('sidebar.failedToDelete'));
      console.error("Error deleting asset list:", error);
      return;
    }

    toast.success(t('sidebar.assetListDeleted'));
    setDeleteDialogOpen(false);
    setEditDialogOpen(false);
    setEditingList(null);
    fetchAssetLists();
    
    // Navigate away if we deleted the current list
    if (location.pathname.includes(editingList.id)) {
      navigate("/app");
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border pt-[calc(env(safe-area-inset-top)+0.5rem)]">
      <SidebarContent>
        <SidebarGroup>
            <div className="flex items-center justify-between px-2">
              <SidebarGroupLabel className="text-lg font-semibold">
                {t('sidebar.assetLists')}
              </SidebarGroupLabel>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="primary" size="icon" className="h-7 w-7">
                    <Plus className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('sidebar.createNewAssetList')}</DialogTitle>
                  </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t('sidebar.title')}</Label>
                    <Input
                      id="title"
                      value={newListTitle}
                      onChange={(e) => setNewListTitle(e.target.value)}
                      placeholder={t('sidebar.titlePlaceholder')}
                      />
                    </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('sidebar.description')}</Label>
                    <Textarea
                      id="description"
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      placeholder={t('sidebar.descriptionPlaceholder')}
                    />
                    </div>
                  <Button
                    onClick={handleCreateList}
                    disabled={isCreating}
                    className="w-full"
                  >
                    {isCreating ? t('sidebar.creating') : t('sidebar.createList')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
              <SidebarGroupContent className="mt-4">
                <SidebarMenu>
                  {isMobileNative && (
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={location.pathname === '/app/quick-access'}>
                        <NavLink to="/app/quick-access" className={() => ""}>
                          <Zap className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate flex-1">{t('sidebar.quickAccess', 'Quick Access')}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === '/app/all'}>
                      <NavLink to="/app/all" className={() => ""}>
                        <Layers className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate flex-1">{t('sidebar.allAssets')}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {isSeoAdmin && (
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={location.pathname === '/app/seo'}>
                        <NavLink to="/app/seo" className={() => ""}>
                          <BarChart3 className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate flex-1">{t('sidebar.seoAnalytics')}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  {isFetchingLists ? (
                    <SidebarMenuItem>
                      <span className="px-6 py-2 text-sm text-muted-foreground">
                        {t('sidebar.loadingAssetLists')}
                      </span>
                    </SidebarMenuItem>
                  ) : assetLists.length === 0 ? (
                    <SidebarMenuItem>
                      <span className="px-6 py-2 text-sm text-muted-foreground">
                        {t('sidebar.noAssetListsYet')}
                      </span>
                    </SidebarMenuItem>
                  ) : (
                    assetLists.map((list) => {
                      const isListActive = location.pathname === `/app/list/${list.id}`;
                      return (
                        <SidebarMenuItem key={list.id}>
                          <SidebarMenuButton asChild isActive={isListActive}>
                            <NavLink to={`/app/list/${list.id}`} className={() => ""}>
                              <List className="h-4 w-4 flex-shrink-0" />
                              <span className="truncate flex-1">{list.title}</span>
                              {list.isShared && (
                                <span className="text-xs text-muted-foreground">({t('sidebar.shared')})</span>
                              )}
                              {!list.isShared && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 ml-auto opacity-0 group-hover/menu-item:opacity-100 hover:bg-sidebar-accent"
                                  onClick={(e) => openEditDialog(list, e)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sidebar.editAssetList')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">{t('sidebar.title')}</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder={t('sidebar.titlePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">{t('sidebar.description')}</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t('sidebar.descriptionPlaceholder')}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isUpdating}
                className="flex-1"
              >
                <Trash2 className="h-4 w-4" />
                {t('sidebar.delete')}
              </Button>
              <Button
                onClick={handleUpdateList}
                disabled={isUpdating}
                className="flex-1"
              >
                {isUpdating ? t('sidebar.saving') : t('sidebar.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar.deleteAssetList')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sidebar.deleteConfirmation', { title: editingList?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('sidebar.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteList}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('sidebar.deleting') : t('sidebar.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sidebar>
  );
}
